/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import * as DT from './TAVRDataTypes';
import type * as MatSDKTypes from '@/engine/WasmModules/MatSDK';
import MatSDK from '@/engine/WasmModules/MatSDK';
import * as THREE from 'three';
import * as MedtronicValve from './MedtronicValveGeneration';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils';
import { emmanaged, emmanaged2 } from '@/engine/PlanningAlgorithm/MatSDKHelpers';
import { coronalOrientationFromDirection, makeTransformation, sliceParams } from '@/engine/PlanningAlgorithm/Helpers';
import {
  CatmullRomCurve3Type,
  createRotatedCoordinateSystem,
  cylinderGeometryFromLine,
  makeCoordinateSystemYz,
  triangleGeometryFromPoints
} from '@/engine/utils/geometryHelper';
import { MakeFunctor } from '@/engine/DataFlow/Functor';
import { ensureDefined, findNearestSegmentPoints } from '@/engine/utils/matSdkHelpers';
import { _catmullRomSpline, _rayCast, _sliceInside } from '../common/CardiacAlgorithmHelpers';

export * from '../common/CardiacAlgorithmHelpers';

export const defaultValveSizeTAVR = MakeFunctor(
  'FT.defaultValveSizeTAVR',
  (system: DT.CardiacSystem, area: DT.AreaMeasurement): DT.StringT => {
    const found = system
      .getAllDevicesAs(DT.TAVRValveDevice)
      .map((d) => {
        const diff = d.metadata.diameter - ((area.dMean ?? 0) - system.metadata.valveSizingTolerance);
        return {
          name: d.name,
          diff: diff < 0 ? 1000 - diff : diff
        };
      })
      .sort((a, b) => a.diff - b.diff);
    if (found.length == 0) {
      throw new Error('Devices are not found');
    }
    return new DT.StringT(found[0].name);
  }
);

export const defaultValvePositionTAVR = MakeFunctor(
  'FT.defaultValvePositionTAVR',
  (
    valveInsertionDepth: DT.NumberT,
    apRotationDeg: DT.NumberT,
    lrRotationDeg: DT.NumberT,
    rotationDeg: DT.NumberT,
    area: DT.AreaMeasurement
  ): DT.TransformMatrix => {
    if (!area.orientationY) {
      throw new Error('Area measurement must have orientationY');
    }
    const devicePlacement = makeCoordinateSystemYz(area.center, area.orientationY, area.normal.clone());
    const inDeviceCsTransformation = createRotatedCoordinateSystem(
      (-apRotationDeg.value * Math.PI) / 180,
      (-lrRotationDeg.value * Math.PI) / 180,
      (-rotationDeg.value * Math.PI) / 180
    ).setPosition(0, 0, -valveInsertionDepth.value - 0.1);
    return new DT.TransformMatrix(devicePlacement.multiply(inDeviceCsTransformation));
  }
);

export const valveAxialPlaneTAVR = MakeFunctor(
  'FT.valveAxialPlaneTAVR',
  (cs: DT.TransformMatrix, coronaryPoint: DT.Point): DT.Plane => {
    const a = new THREE.Vector3(0, 0, 0).applyMatrix4(cs.value);
    const b = new THREE.Vector3(0, 0, 1).applyMatrix4(cs.value);
    const c = coronaryPoint.value.clone().addScaledVector(b.clone().sub(a), 2);
    const plane = new THREE.Plane().setFromCoplanarPoints(a, b, c);
    return new DT.Plane({ planeEq: plane, origin: c });
  }
);

export const coronaryRiskPointsTAVR = MakeFunctor(
  'FT.coronaryRiskPointsTAVR',
  (aortaSurface: DT.MatTriangleSurface, slicingPlane: DT.Plane, topPlane: DT.Plane): DT.Polyline => {
    const pl = slicingPlane.value.planeEq;
    const bottomPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      topPlane.value.planeEq.normal,
      slicingPlane.value.origin
    );
    const origin = bottomPlane.projectPoint(topPlane.value.origin, new THREE.Vector3());
    const vecZ = slicingPlane.value.planeEq.normal.clone().normalize();
    const vecY = vecZ.clone().cross(topPlane.value.planeEq.normal).normalize();
    const vecX = vecY.clone().cross(vecZ).normalize();
    const qcs = makeTransformation(origin, vecX, vecY, vecZ).invert();
    const distX = 20;
    const distY = slicingPlane.value.origin.clone().applyMatrix4(qcs).y + 10;
    const ret = emmanaged(
      emmanaged(new MatSDK.Double4().set(pl.normal.x, pl.normal.y, pl.normal.z, pl.constant), (planeEq) =>
        MatSDK.slice(aortaSurface.value, planeEq, false, 0.0)
      ),
      (lines) =>
        emmanaged(MatSDK.splitLineSegmentsToPolylines(lines, true), (vectorPolylines3d) => {
          const pts: Array<THREE.Vector3> = [];
          const distances: Array<{ idx: number; dist: number }> = [];
          for (let i = 0, sz = vectorPolylines3d.size(); i < sz; ++i) {
            const polyline3d = vectorPolylines3d.access(i);
            const isInside = emmanaged(
              emmanaged(new MatSDK.MatrixDouble4x4().setFromArray(qcs.toArray()), (matrix) =>
                polyline3d.transformed(matrix)
              ),
              (polyline3dNew) =>
                emmanaged(polyline3dNew.projectedOnXY(), (polyline2d) =>
                  emmanaged(new MatSDK.Double2().set(0, 0), (centerPoint) => {
                    if (!polyline2d.isInside(centerPoint)) {
                      const d = emmanaged(polyline2d.center(), (centroid) => {
                        return Math.sqrt(centroid.x * centroid.x + centroid.y * centroid.y);
                      });
                      distances.push({ idx: i, dist: d });
                      return false;
                    }
                    const pts3d = polyline3d.accessPoints();
                    const pts2d = polyline2d.accessPoints();
                    for (let j = 0, cnt = pts2d.size(); j < cnt; ++j) {
                      const pt2d = pts2d.access(j);
                      if (pt2d.x < 0 || pt2d.x > distX || pt2d.y < 0 || pt2d.y > distY) continue;
                      const pt3d = pts3d.access(j);
                      pts.push(new THREE.Vector3().fromArray(pt3d.toArray()));
                    }
                    return true;
                  })
                )
            );
            if (isInside) {
              break;
            }
          }
          if (pts.length > 0 || distances.length == 0) {
            return pts;
          }
          // bad contours fallback mode
          distances.sort((a, b) => a.dist - b.dist);
          const polyline3d = vectorPolylines3d.access(distances[0].idx);
          emmanaged(
            emmanaged(new MatSDK.MatrixDouble4x4().setFromArray(qcs.toArray()), (matrix) =>
              polyline3d.transformed(matrix)
            ),
            (polyline3dNew) =>
              emmanaged(polyline3dNew.projectedOnXY(), (polyline2d) => {
                const pts3d = polyline3d.accessPoints();
                const pts2d = polyline2d.accessPoints();
                for (let j = 0, cnt = pts2d.size(); j < cnt; ++j) {
                  const pt2d = pts2d.access(j);
                  if (pt2d.x < 0 || pt2d.x > distX || pt2d.y < 0 || pt2d.y > distY) continue;
                  const pt3d = pts3d.access(j);
                  pts.push(new THREE.Vector3().fromArray(pt3d.toArray()));
                }
              })
          );
          return pts;
        })
    );

    return new DT.Polyline({ points: ret, isClosed: false });
  }
);

export const calculateVTC_TAVR = MakeFunctor(
  'FT.calculateVtcTAVR',
  (device: DT.TAVRValveDevice, cs: DT.TransformMatrix, coronaryPoint: DT.Point): DT.Line => {
    const origin = new THREE.Vector3(0, 0, 0).applyMatrix4(cs.value);
    const vecZ = new THREE.Vector3(0, 0, 1).transformDirection(cs.value);
    const pt = coronaryPoint.value.clone().addScaledVector(vecZ, 2);
    const vecX = pt.clone().sub(origin).cross(vecZ).negate().cross(vecZ).normalize();
    const points: Array<THREE.Vector3> = device.metadata.riskContourPoints.map((value) =>
      origin.clone().add(vecX.clone().multiplyScalar(value[0])).add(vecZ.clone().multiplyScalar(value[1]))
    );
    const lines: Array<THREE.Line3> = [];
    lines.push(new THREE.Line3(points[0].clone().addScaledVector(vecZ, -100), points[0]));
    for (let i = 1, cnt = points.length; i < cnt; ++i) {
      lines.push(new THREE.Line3(points[i - 1], points[i]));
    }
    lines.push(
      new THREE.Line3(points[points.length - 1], points[points.length - 1].clone().addScaledVector(vecZ, 100))
    );
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(vecZ, pt);
    let intersect: THREE.Vector3 | undefined = undefined;
    for (const ln of lines) {
      const point = plane.intersectLine(ln, new THREE.Vector3());
      if (point !== null) {
        intersect = point;
        break;
      }
    }
    if (intersect) {
      return new DT.Line(new THREE.Line3(pt, intersect));
    } else {
      const ln = new THREE.Line3(origin, origin.clone().add(vecZ));
      const proj = ln.closestPointToPoint(pt, false, new THREE.Vector3());
      const vec = new THREE.Vector3().subVectors(pt, proj).normalize();
      proj.addScaledVector(vec, device.metadata.diameter / 2);
      return new DT.Line(new THREE.Line3(pt, proj));
    }
  }
);

export const calculateVTA_cylinder_TAVR = MakeFunctor(
  'FT.calculateVtaCylinderTAVR',
  (device: DT.TAVRValveDevice, cs: DT.TransformMatrix, pts: DT.Polyline): DT.Line => {
    const ln = new THREE.Line3(
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 0, device.metadata.distanceToRiskPlane)
    ).applyMatrix4(cs.value);
    const vecZ = ln.delta(new THREE.Vector3());
    const vecX = pts.value.points[0].clone().sub(ln.start).cross(vecZ).negate().cross(vecZ).normalize();
    vecX.multiplyScalar(device.metadata.diameter / 2);
    ln.start.add(vecX);
    ln.end.add(vecX);
    const min = new THREE.Line3(new THREE.Vector3(0, 0, 0), new THREE.Vector3(10000, 0, 0));
    for (const pt of pts.value.points) {
      const proj = ln.closestPointToPoint(pt, true, new THREE.Vector3());
      if (min.distanceSq() > proj.distanceToSquared(pt)) {
        min.set(pt, proj);
      }
    }
    return new DT.Line(min);
  }
);

export const calculateVTA_TAVR = MakeFunctor(
  'FT.calculateVtaTAVR',
  (device: DT.TAVRValveDevice, cs: DT.TransformMatrix, pts: DT.Polyline): DT.Line => {
    const origin = new THREE.Vector3(0, 0, 0).applyMatrix4(cs.value);
    const vecZ = new THREE.Vector3(0, 0, 1).transformDirection(cs.value);
    const vecX = pts.value.points[0].clone().sub(origin).cross(vecZ).negate().cross(vecZ).normalize();
    const points: Array<THREE.Vector3> = device.metadata.riskContourPoints.map((value) =>
      origin.clone().add(vecX.clone().multiplyScalar(value[0])).add(vecZ.clone().multiplyScalar(value[1]))
    );
    const lines: Array<THREE.Line3> = [];
    for (let i = 1, cnt = points.length; i < cnt; ++i) {
      lines.push(new THREE.Line3(points[i - 1], points[i]));
    }
    const min = new THREE.Line3(new THREE.Vector3(0, 0, 0), new THREE.Vector3(10000, 0, 0));
    for (const pt of pts.value.points) {
      for (const ln of lines) {
        const proj = ln.closestPointToPoint(pt, true, new THREE.Vector3());
        if (min.distanceSq() > proj.distanceToSquared(pt)) {
          min.set(pt, proj);
        }
      }
    }
    return new DT.Line(min);
  }
);

export const vtaVtcRoiTAVR = MakeFunctor(
  'FT.vtaVtcRoiTAVR',
  (cs: DT.TransformMatrix, pt: DT.Point, dir: DT.Vector): DT.RegionOfInterest => {
    const origin = new THREE.Vector3(0, 0, 0).applyMatrix4(cs.value);
    const vecZ = new THREE.Vector3(0, 0, 1).transformDirection(cs.value);
    const axis = new THREE.Line3(origin, origin.clone().add(vecZ));
    const ptO = axis.closestPointToPoint(pt.value, false, new THREE.Vector3());
    const plane = new THREE.Plane().setFromCoplanarPoints(axis.start, axis.end, pt.value);
    const vecY = plane.normal.clone().normalize();
    if (vecY.dot(dir.value) < 0) vecY.negate();
    const vecX = vecY.clone().cross(vecZ).normalize();
    const transformFrom = makeTransformation(ptO, vecX, vecY, vecZ);
    const transformTo = transformFrom.clone().invert();
    const ptX = pt.value.clone().applyMatrix4(transformTo).x;
    const minCorner = new THREE.Vector3(-ptX * 1.15, -ptX * 1.15, -15).applyMatrix4(transformFrom);
    const maxCorner = new THREE.Vector3(ptX * 1.15, ptX * 1.15, 15).applyMatrix4(transformFrom);
    return new DT.RegionOfInterest({
      ptO: ptO,
      dirZ: vecZ,
      dirY: vecY,
      minCorner: minCorner,
      maxCorner: maxCorner
    });
  }
);

export const sovRoiTAVR = MakeFunctor(
  'FT.sovRoiTAVR',
  (sovPlane: DT.Plane, lccLine: DT.Line, rccLine: DT.Line, nccLine: DT.Line): DT.RegionOfInterest => {
    const orientation = coronalOrientationFromDirection(sovPlane.value.planeEq.normal);
    const vecX = orientation.orientationY.clone().cross(orientation.orientationZ).normalize();
    const transformFrom = makeTransformation(
      sovPlane.value.origin,
      vecX,
      orientation.orientationY,
      orientation.orientationZ
    );
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(lccLine.value.start.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(lccLine.value.end.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(rccLine.value.start.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(rccLine.value.end.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(nccLine.value.start.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(nccLine.value.end.clone().applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(0, 0, 10));
    bBox.max.add(new THREE.Vector3(0, 0, 10));
    const minCorner = bBox.min.applyMatrix4(transformFrom);
    const maxCorner = bBox.max.applyMatrix4(transformFrom);
    return new DT.RegionOfInterest({
      ptO: sovPlane.value.origin.clone(),
      dirZ: orientation.orientationZ,
      dirY: orientation.orientationY,
      minCorner: minCorner,
      maxCorner: maxCorner
    });
  }
);

export const coronaryPosteriorDirectionTAVR = MakeFunctor(
  'FT.coronaryPosteriorDirectionTAVR',
  (pl: DT.Plane, ptL: DT.Point, ptR: DT.Point): DT.Vector => {
    const topDir = pl.value.planeEq.normal.clone().negate();
    const rightDir = ptR.value.clone().sub(ptL.value);
    return new DT.Vector(topDir.cross(rightDir).normalize());
  }
);

export const heartOrientationTAVR = MakeFunctor(
  'FT.heartOrientationTAVR',
  (rcc: DT.Point, lcc: DT.Point, ncc: DT.Point, top: DT.Vector): DT.Orientation => {
    const vecPos = lcc.value.clone().add(ncc.value).divideScalar(2).sub(rcc.value).normalize();
    const vecTop = vecPos.clone().cross(top.value).cross(vecPos);
    return new DT.Orientation({ top: vecTop, dir: vecPos });
  }
);

export const cuspOverlapOrientationTAVR = MakeFunctor(
  'FT.cuspOverlapOrientationTAVR',
  (rcc: DT.Point, lcc: DT.Point, top: DT.Vector): DT.Orientation => {
    const vecPos = lcc.value.clone().sub(rcc.value).normalize();
    const vecTop = vecPos.clone().cross(top.value).cross(vecPos);
    return new DT.Orientation({ top: vecTop, dir: vecPos });
  }
);

export const generateValveDynamicPartTAVR = MakeFunctor(
  'FT.generateValveDynamicPartTAVR',
  (
    device: DT.TAVRValveDevice,
    deviceCs: DT.TransformMatrix,
    tree: DT.MatKDTree3d,
    surf: DT.MatTriangleSurface,
    shouldMorph: DT.BooleanT
  ): DT.Mesh => {
    const deviceToSurface = deviceCs.value;
    const surfaceToDevice = deviceToSurface.clone().invert();
    const layers = device.metadata.layers;
    if (layers.length == 0) {
      return new DT.Mesh(new THREE.BufferGeometry());
    } else {
      let pts = MedtronicValve.pointCloud(
        layers,
        device.metadata.numPointsInLayer,
        device.metadata.numStaticLayers - 1
      );
      if (shouldMorph.value) {
        const safetyDistance = 1.2; // we will subtract 1.2mm from projected point to be inside the aorta
        const morphedPts = new Array<Array<THREE.Vector3>>(pts.length);
        let isMorphingOk = true;
        morphedPts[0] = pts[0];
        for (let i = 1; isMorphingOk && i < pts.length; ++i) {
          const layer = pts[i];
          morphedPts[i] = new Array<THREE.Vector3>(layer.length);
          for (let j = 0; isMorphingOk && j < layer.length; ++j) {
            const pt = layer[j];
            const radius = Math.sqrt(pt.x * pt.x + pt.y * pt.y);
            const direction = new THREE.Vector3(pt.x, pt.y, 0).transformDirection(deviceToSurface).normalize();
            const origin = new THREE.Vector3(0, 0, pt.z).applyMatrix4(deviceToSurface);
            isMorphingOk = emmanaged2(
              new MatSDK.Double3().setFromArray(origin.toArray()),
              new MatSDK.Double3().setFromArray(direction.toArray()),
              (o: MatSDKTypes.Double3, d: MatSDKTypes.Double3) =>
                emmanaged(new MatSDK.Ray().set(o, d), (ray) => {
                  const results = tree.value.intersectionsTriangleSurface(surf.value, ray);
                  let l = -1;
                  let minDist = -1;
                  for (let k = 0; k < results.size(); ++k) {
                    const res = ensureDefined(results.get(k));
                    if (minDist < 0 || res.distance < minDist) {
                      minDist = res.distance;
                      l = k;
                    }
                  }
                  const result = ensureDefined(results.get(l));
                  if (result.distance >= radius + safetyDistance) {
                    morphedPts[i][j] = pt;
                    return true;
                  } else {
                    if (result.distance >= radius / 4 + safetyDistance) {
                      const proj = new THREE.Vector3().fromArray(result.point.toArray());
                      morphedPts[i][j] = proj.addScaledVector(direction, -safetyDistance).applyMatrix4(surfaceToDevice);
                      return true;
                    } else {
                      return false;
                    }
                  }
                })
            );
          }
        }
        if (isMorphingOk) {
          pts = morphedPts;
        }
      }
      const wireframe = MedtronicValve.wireframe(pts, device.metadata.numStaticLayers - 1);
      const geoms: Array<THREE.BufferGeometry> = [];
      for (const layer of wireframe) {
        for (const ln of layer) {
          geoms.push(cylinderGeometryFromLine(ln, device.metadata.wireRadius));
        }
      }
      const triangles = MedtronicValve.triangles(pts, device.metadata.numStaticLayers - 1);
      for (let i = 0; i < 2; ++i) {
        const layer = triangles[i];
        for (let j = 0; j < layer.length; ++j) {
          if (
            (device.metadata.numStaticLayers % 2 == 1 && (j + 2) % 10 == 0) ||
            (device.metadata.numStaticLayers % 2 == 0 && (j + 3) % 8 == 0)
          ) {
            const tri = layer[j];
            geoms.push(triangleGeometryFromPoints(tri, true));
            if (i == 0) {
              const pt0 = tri[0];
              const pt1 = pt0.clone().setX(0).setY(0);
              const pt2 = tri[1].clone().add(tri[2]).divideScalar(2);
              geoms.push(triangleGeometryFromPoints([pt0, pt1, pt2], true));
            }
          }
        }
      }
      const torus = new THREE.TorusGeometry(device.metadata.wireRadius * 5, device.metadata.wireRadius).deleteAttribute(
        'uv'
      );
      const pt = pts[pts.length - 1][device.metadata.numStaticLayers % 2 == 0 ? 2 : 4];
      geoms.push(
        torus
          .rotateY(Math.PI / 2)
          .rotateZ(
            device.metadata.numStaticLayers % 2 == 0
              ? Math.PI / 2 + Math.PI / device.metadata.numPointsInLayer
              : Math.PI / 2
          )
          .translate(pt.x, pt.y, pt.z + device.metadata.wireRadius * 6)
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment
      const ret = mergeGeometries(geoms);
      return new DT.Mesh(ret);
    }
  }
);

export const getAvailableValveDevicesTAVR = MakeFunctor(
  'FT.getAvailableValveDevicesTAVR',
  (system: DT.CardiacSystem): DT.IdentifiersList => {
    return new DT.IdentifiersList(system.getAvailableDevicesNames(DT.TAVRValveDevice.DeviceType));
  }
);

export const getValveDeviceTAVR = MakeFunctor(
  'FT.getValveDeviceTAVR',
  (system: DT.CardiacSystem, device: DT.StringT): DT.TAVRValveDevice => {
    return system.getDeviceAs(DT.TAVRValveDevice, device.value);
  }
);

export const valveBottomPlaneTAVR = MakeFunctor('FT.valveBottomPlaneTAVR', (cs: DT.TransformMatrix): DT.Plane => {
  const origin = new THREE.Vector3(0, 0, 0).applyMatrix4(cs.value);
  const normal = new THREE.Vector3(0, 0, 1).transformDirection(cs.value);
  const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, origin);
  return new DT.Plane({ planeEq: plane, origin: origin });
});

export const valveRiskPlaneTAVR = MakeFunctor(
  'FT.valveRiskPlaneTAVR',
  (device: DT.TAVRValveDevice, cs: DT.TransformMatrix): DT.Plane => {
    const origin = new THREE.Vector3(0, 0, device.metadata.distanceToRiskPlane).applyMatrix4(cs.value);
    const normal = new THREE.Vector3(0, 0, 1).transformDirection(cs.value);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, origin);
    return new DT.Plane({ planeEq: plane, origin: origin });
  }
);

function _anatomicalSpline(
  surf: MatSDKTypes.TriangleSurface,
  preliminarySpline: Array<THREE.Vector3>,
  accuracyMm: number
): Array<THREE.Vector3> {
  const coords: Array<number> = [];
  for (let i = 1; i < preliminarySpline.length - 1; ++i) {
    const origin = preliminarySpline[i];
    const normal = preliminarySpline[i + 1].clone().sub(origin);
    const pt = sliceParams(_sliceInside(surf, normal, origin))?.center;
    if (!pt) {
      continue;
    }
    coords.push(pt.x, pt.y, pt.z);
  }
  const pt1 = new THREE.Vector3().fromArray(coords, 0).add(preliminarySpline[0].clone().sub(preliminarySpline[1]));
  coords.unshift(pt1.x, pt1.y, pt1.z);
  const pt2 = preliminarySpline[preliminarySpline.length - 1];
  coords.push(pt2.x, pt2.y, pt2.z);

  const pts = emmanaged(new MatSDK.Polyline3d(), (polyline) => {
    polyline.setFromRaw(coords, false);
    return emmanaged(polyline.smoothed(3, 0.9), (smoothPoly) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const rawPts = smoothPoly.getRawPoints();
      const ret = new Array<THREE.Vector3>(rawPts.length / 3);
      for (let i = 0, j = 0; i < rawPts.length; i += 3, j++) {
        ret[j] = new THREE.Vector3().fromArray(rawPts, i);
      }
      return ret;
    });
  });

  let len = 0;
  for (let i = 1; i < pts.length; ++i) {
    len += pts[i].distanceTo(pts[i - 1]);
  }

  const num = Math.round(len / accuracyMm);
  const spline = new THREE.CatmullRomCurve3(pts, false, CatmullRomCurve3Type.Chordal);
  return spline.getSpacedPoints(num);
}

export const makePolylineFromAreaMeasTAVR = MakeFunctor(
  'FT.makePolylineFromAreaMeasTAVR',
  (
    aorta: DT.MatTriangleSurface,
    leftVentricle: DT.MatTriangleSurface,
    aorticAnnulusPlane: DT.Plane,
    ascAortaPlane: DT.Plane,
    ptLc: DT.Point,
    ptRc: DT.Point
  ): DT.Polyline => {
    const accuracyMm = 0.25;

    const annulusParams = sliceParams(
      _sliceInside(leftVentricle.value, aorticAnnulusPlane.value.planeEq.normal, aorticAnnulusPlane.value.origin)
    );
    const ascAortaParams = sliceParams(
      _sliceInside(aorta.value, ascAortaPlane.value.planeEq.normal, ascAortaPlane.value.origin)
    );

    if (!annulusParams || !ascAortaParams) {
      throw new Error('Slicing failed');
    }

    if (annulusParams.normal.dot(ascAortaParams.center.clone().sub(annulusParams.center)) < 0) {
      annulusParams.normal.negate();
    }
    if (ascAortaParams.normal.dot(ascAortaParams.center.clone().sub(annulusParams.center)) < 0) {
      ascAortaParams.normal.negate();
    }

    // STJ to Ascending Aorta Spline
    const annulusToAscAortaSplinePre = _catmullRomSpline(
      annulusParams.center,
      annulusParams.normal,
      ascAortaParams.center,
      ascAortaParams.normal,
      1.0
    );
    const found = findNearestSegmentPoints(annulusToAscAortaSplinePre, [
      ptLc.value.clone().addScaledVector(annulusParams.normal, 6),
      ptRc.value.clone().addScaledVector(annulusParams.normal, 6)
    ]);
    const idx = Math.max(found[0].segmentIndex, found[1].segmentIndex);
    const stjToAscAortaSplinePre = annulusToAscAortaSplinePre.slice(idx);
    const stjToAscAortaSpline = _anatomicalSpline(aorta.value, stjToAscAortaSplinePre, accuracyMm);

    // AorticAnnulus to STJ spline
    const annulusToStjSpline = _catmullRomSpline(
      annulusParams.center,
      annulusParams.normal,
      stjToAscAortaSpline[0],
      stjToAscAortaSpline[1].clone().sub(stjToAscAortaSpline[0]).normalize(),
      accuracyMm
    );
    annulusToStjSpline.pop();

    // LVOT to Aortic Annulus (perpendicular)
    const lvotToAnnulusSpline: Array<THREE.Vector3> = [];
    for (let i = 0; i < 6; i += 0.25) {
      lvotToAnnulusSpline.unshift(annulusParams.center.clone().addScaledVector(annulusParams.normal, -i));
    }
    lvotToAnnulusSpline.pop();

    // Stitch splines
    const splinePts = lvotToAnnulusSpline.concat(annulusToStjSpline, stjToAscAortaSpline);

    // Result
    return new DT.Polyline({ points: splinePts, isClosed: false });
  }
);

export const makeAortaLvCenterlineTAVR = MakeFunctor(
  'FT.makeAortaLvCenterlineTAVR',
  (
    aorta: DT.MatTriangleSurface,
    leftVentricle: DT.MatTriangleSurface,
    aorticAnnulusPlane: DT.Plane,
    stjPlane: DT.Plane,
    ascAortaPlane: DT.Plane
  ): DT.Polyline => {
    const accuracyMm = 0.25;

    const annulusParams = sliceParams(
      _sliceInside(leftVentricle.value, aorticAnnulusPlane.value.planeEq.normal, aorticAnnulusPlane.value.origin)
    );
    const stjParams = sliceParams(_sliceInside(aorta.value, stjPlane.value.planeEq.normal, stjPlane.value.origin));
    const ascAortaParams = sliceParams(
      _sliceInside(aorta.value, ascAortaPlane.value.planeEq.normal, ascAortaPlane.value.origin)
    );

    if (!annulusParams || !ascAortaParams || !stjParams) {
      throw new Error('Slicing failed');
    }

    if (annulusParams.normal.dot(ascAortaParams.center.clone().sub(annulusParams.center)) < 0) {
      annulusParams.normal.negate();
    }
    if (stjParams.normal.dot(ascAortaParams.center.clone().sub(stjParams.center)) < 0) {
      stjParams.normal.negate();
    }
    if (ascAortaParams.normal.dot(ascAortaParams.center.clone().sub(annulusParams.center)) < 0) {
      ascAortaParams.normal.negate();
    }

    // STJ to Ascending Aorta Spline
    const stjToAscAortaSplinePre = _catmullRomSpline(
      stjParams.center.clone().addScaledVector(stjParams.normal, -1),
      stjParams.normal,
      ascAortaParams.center,
      ascAortaParams.normal,
      1.0
    );
    const stjToAscAortaSpline = _anatomicalSpline(aorta.value, stjToAscAortaSplinePre, accuracyMm);
    const center = stjToAscAortaSpline[stjToAscAortaSpline.length - 1];
    const normal = stjToAscAortaSpline[stjToAscAortaSpline.length - 2].clone().sub(center).normalize();
    for (let i = 0; i < 10; i += accuracyMm) {
      stjToAscAortaSpline.push(center.clone().addScaledVector(normal, -i));
    }

    // Annulus to STJ spline
    const sovToStjSpline = _catmullRomSpline(
      annulusParams.center,
      annulusParams.normal,
      stjToAscAortaSpline[0],
      stjToAscAortaSpline[1].clone().sub(stjToAscAortaSpline[0]).normalize(),
      accuracyMm
    );
    sovToStjSpline.pop();

    // LVOT to Annulus (perpendicular)
    const lvotToAnnulusSpline: Array<THREE.Vector3> = [];
    for (let i = 0; i < 14; i += accuracyMm) {
      lvotToAnnulusSpline.unshift(annulusParams.center.clone().addScaledVector(annulusParams.normal, -i));
    }
    lvotToAnnulusSpline.pop();

    // Stitch splines
    const splinePts = lvotToAnnulusSpline.concat(sovToStjSpline, stjToAscAortaSpline);

    return new DT.Polyline({ points: splinePts, isClosed: false });
  }
);

export const adjustSovPointTAVR = MakeFunctor(
  'FT.adjustSovPointTAVR',
  (
    aorta: DT.MatTriangleSurface,
    aortaTree: DT.MatKDTree3d,
    sovPlane: DT.Plane,
    sovPoint: DT.Point,
    sovPointSecondary: DT.Point
  ): DT.Point => {
    const pt = sovPlane.value.planeEq.projectPoint(sovPoint.value, new THREE.Vector3());
    const ptSec = sovPlane.value.planeEq.projectPoint(sovPointSecondary.value, new THREE.Vector3());
    const origin = pt.clone().add(ptSec).divideScalar(2);
    const direction = pt.clone().sub(ptSec).normalize();
    const ret = _rayCast(aortaTree.value, aorta.value, origin, direction);
    if (ret) {
      const d1 = pt.distanceTo(ptSec);
      const d2 = ret.distanceTo(ptSec);
      if (Math.abs(d1 - d2) / d1 < 0.15) {
        return new DT.Point(ret);
      }
    }
    return new DT.Point(pt);
  }
);

export const calculateAorticAngulationTAVR = MakeFunctor(
  'FT.calculateAorticAngulationTAVR',
  (annulus: DT.AreaMeasurement): DT.AngleMeasurement => {
    const dir = new THREE.Vector3(0, 1, 0).cross(annulus.normal).normalize();

    const transfFrom = makeTransformation(
      annulus.center,
      dir,
      new THREE.Vector3(0, 1, 0).cross(dir).normalize(),
      new THREE.Vector3(0, 1, 0)
    );
    const transfTo = transfFrom.clone().invert();

    let minX = 1;
    let maxX = -1;
    for (const pt of annulus.splinePts) {
      const x = pt.clone().applyMatrix4(transfTo).x;
      if (x > maxX) {
        maxX = x;
      } else if (x < minX) {
        minX = x;
      }
    }

    const pt1 = new THREE.Vector3(maxX, 0, 0).applyMatrix4(transfFrom);
    const ptMid = new THREE.Vector3(minX, 0, 0).applyMatrix4(transfFrom);
    const pt2 = ptMid.clone().addScaledVector(new THREE.Vector3(1, 0, 0), ptMid.distanceTo(pt1));

    const angleRad = pt1.clone().sub(ptMid).angleTo(pt2.clone().sub(ptMid));

    const bBox = new THREE.Box3();
    bBox.expandByPoint(pt1);
    bBox.expandByPoint(ptMid.clone().sub(pt1).add(ptMid));
    bBox.expandByPoint(pt2);
    bBox.expandByPoint(ptMid.clone().sub(pt2).add(ptMid));
    const location: DT.RegionOfInterestT = {
      ptO: ptMid.clone(),
      dirZ: new THREE.Vector3(0, 0, 1),
      dirY: new THREE.Vector3(0, 1, 0),
      minCorner: bBox.min.sub(new THREE.Vector3(0, 10, 0)),
      maxCorner: bBox.max.add(new THREE.Vector3(0, 10, 0))
    };

    return new DT.AngleMeasurement(angleRad, ptMid, pt1, pt2, location);
  }
);
